import React, { useState } from "react";
import AccordionTwoColumnGrid from "@/components/molecules/AccordionTwoColumnGrid";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Avatar, Card, Grid } from "@mantine/core";
import TextInput from "@/components/atoms/TextInput";
import { Control, Controller, useFieldArray, useFormState } from "react-hook-form";
import { RegisterCompanyFormInput } from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";
import CTA from "@/components/atoms/CTA";
import SelectRoleType from "@/components/molecules/SelectInputs/SelectRoleType";
import Group from "@/components/atoms/Group";
import Badge from "@/components/atoms/Badge";
import { firstErrorMessage } from "@/utils/formErrors";

interface TeamProps {
  control: Control<RegisterCompanyFormInput>;
  saveDraft: () => Promise<void>;
}

const emptyMember: {
  firstname: string;
  lastname: string;
  email: string;
  roleType: string;
} = {
  firstname: "",
  lastname: "",
  email: "",
  roleType: "ADMIN",
};

const Team: React.FC<TeamProps> = ({ control, saveDraft }) => {
  const [newMember, setNewMember] = useState(emptyMember);
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "partialCompany.teamMembers" as any,
  });
  const { errors } = useFormState({ control, name: "partialCompany.teamMembers" });
  const memberErrors = errors.partialCompany?.teamMembers;

  const handleAdd = async () => {
    append(newMember as any);
    setNewMember(emptyMember);
    await saveDraft();
  };

  const leftContent = (
    <Stack>
      <Text size="lg" display="technical" tt="uppercase" fw={500} ff="heading" mt={20}>
        {t("registerCompany:team.title")}
      </Text>
      <Grid>
        <Grid.Col span={1} pr={0} mr={0}>
          <SVGAsset name="circle-questionmark" />
        </Grid.Col>
        <Grid.Col span={10} pl={0} ml={0}>
          <Text size="sm" color="secondary">
            {t("registerCompany:team.reason")}
          </Text>
        </Grid.Col>
      </Grid>
    </Stack>
  );

  const rightContent = (
    <Grid>
      <Grid.Col span={12}>
        <Text size="sm" strong>
          {t("registerCompany:team.you")}
        </Text>
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Controller
          name="partialCompany.admin.firstname"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <TextInput
              data-testid="admin-firstname-input"
              label={t("registerCompany:team.firstName")}
              withAsterisk
              value={value ?? ""}
              onChange={onChange}
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Controller
          name="partialCompany.admin.lastname"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <TextInput
              data-testid="admin-lastname-input"
              label={t("registerCompany:team.lastName")}
              withAsterisk
              value={value ?? ""}
              onChange={onChange}
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={12} mt="md">
        <Text size="sm" strong>
          {t("registerCompany:team.otherUsers")}
        </Text>
      </Grid.Col>

      <Grid.Col span={12}>
        <Group>
          {fields.map((field: any, idx) => (
            <Card key={field.id} shadow="xs" padding="lg" radius="md" withBorder pos={"relative"}>
              <Badge pos={"absolute"} radius={0} top={0} right={0}>
                {t(`enumerables:userRoles.${field.roleType}`)}
              </Badge>
              <Avatar color="blue" radius="xl" size="lg" mx={"auto"}>
                {(field.firstname?.[0] || "").toUpperCase()}
                {(field.lastname?.[0] || "").toUpperCase()}
              </Avatar>
              <Stack align={"center"} gap={2} mt={"sm"}>
                <Text size="sm" color="primary" fw={500}>
                  {field.firstname} {field.lastname}
                </Text>
                <Text size={"sm"} color="secondary" ta={"center"} mx={"auto"}>
                  {field.email}
                </Text>
                {firstErrorMessage(memberErrors?.[idx]) && (
                  <Text size="sm" color="error" data-testid={`team-member-error-${idx}`}>
                    {t(firstErrorMessage(memberErrors?.[idx]) as string)}
                  </Text>
                )}
                <CTA intent={"negate"} size={"xs"} mt="sm" onClick={() => remove(idx)}>
                  {t("registerCompany:team.removeUser")}
                </CTA>
              </Stack>
            </Card>
          ))}
        </Group>
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          data-testid={"team-firstname-input"}
          label={t("registerCompany:team.firstName")}
          withAsterisk
          value={newMember.firstname}
          onChange={(e) => setNewMember({ ...newMember, firstname: e.target.value })}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          data-testid={"team-lastname-input"}
          label={t("registerCompany:team.lastName")}
          withAsterisk
          value={newMember.lastname}
          onChange={(e) => setNewMember({ ...newMember, lastname: e.target.value })}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          data-testid={"team-email-input"}
          label={t("registerCompany:team.email")}
          withAsterisk
          value={newMember.email}
          onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <SelectRoleType
          data-testid={"team-role-select"}
          label={t("registerCompany:team.role")}
          value={newMember.roleType}
          onChange={(val) => setNewMember({ ...newMember, roleType: val as string })}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <CTA intent="secondary" data-testid="team-add-button" onClick={handleAdd}>
          {t("registerCompany:team.addNewUser")}
        </CTA>
      </Grid.Col>
    </Grid>
  );

  return <AccordionTwoColumnGrid leftContent={leftContent} rightContent={rightContent} />;
};

export default Team;
